import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import { ModalErrorMessage } from "@/common/components/ModalErrorMessage";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { ErrorResponse } from "@/common/types/errorResponse";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";
import { Plug, SearchIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { defaultColumns } from "../../constants/posColumns.constant";
import { options as statusOptions } from "../../constants/status.constant";
import { usePosMachines } from "../../hooks/usePosMachines";
import { usePosRegistration } from "../../hooks/usePosRegistration";
import { PosTransformer } from "../../services/posTransformer";
import { PosData, PosTableItem } from "../../types/pos.type";

const PosList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const navigate = useNavigate();

  const [modalErrorData, setModalErrorData] = useState<ErrorResponse | boolean>(false);
  const [searchEstablishmentCompanies, setSearchEstablishmentCompanies] = useDebounce('');

  const { data: establishmentCompaniesOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishmentCompanies },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer()
    })
  });

  const {
    data: posMachines,
    meta,
    isLoading: isLoadingPosMachines,
    error: posMachinesError = {} as any,
    refetch: refetchPosMachines,
  } = usePosMachines({
    filters: {
      page: filterParams.page || 1,
      search: search,
      status: filterParams.status?.value,
      establishmentCompanyBranchId: filterParams.establishmentCompanyBranch?.value,
    },
    mapper: Mapper.mapWithTransformer<PosData[], PosTableItem[]>({
      transformer: PosTransformer.tableTransformer("Admin::Pos::Machines::Filter")
    })
  });

  const { mutate: createPosRegistration, isPending: createPosRegistrationIsPending } = usePosRegistration({
    onSuccess: (response) => {
      navigate(`/pos/qrcode/${response.data.data.id}`)
    },
    onError: (response) => {
      setModalErrorData(response);
    },
  })

  return (
    <div>
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <ListHeader
            Icon={<Plug className="w-10 h-10" strokeWidth={1.5} />}
            title="POS"
          />
          <Button
            className="py-2 px-4 rounded-sm cursor-pointer bg-primary-blue hover:bg-primary-blue-hover shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] whitespace-nowrap"
            onClick={() => { createPosRegistration(); }}
            isLoading={createPosRegistrationIsPending}
            operationKlassToCheckPermission='Admin::Pos::Registrations::Create'
          >
            Novo POS
          </Button>
        </div>

        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={posMachinesError?.status}
          refetchFunction={refetchPosMachines}
        >
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <TextField
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                onChange={(e) => {
                  setSearch((e.target as HTMLInputElement).value);
                  setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
                }
                }
                value={filterParams.search}
              />

              <ListFilters
                filters={[
                  {
                    className: 'min-w-44',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, status: (e as { value?: string; })?.value ? e : null })),
                    value: filterParams.status,
                    options: [{ label: 'Todos', value: '' }, ...(statusOptions || [])],
                    placeholder: 'Status'
                  },
                  {
                    className: 'min-w-44',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, establishmentCompanyBranch: (e as { value?: string; })?.value ? e : null })),
                    value: filterParams.establishmentCompanyBranch,
                    options: [{ label: 'Todos', value: '' }, ...(establishmentCompaniesOptions || [])],
                    placeholder: 'Estabelecimento',
                    onInputChange: (value) => setSearchEstablishmentCompanies(value)
                  },
                ]}
              />
            </div>

            <Table
              columns={defaultColumns}
              isLoadingData={isLoadingPosMachines}
              page={filterParams.page || 1}
              rows={posMachines}
              totalPages={meta.total_pages}
              totalCount={meta.total}
              hasPagination
              setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
              rowEmptyValue="--"
            />
          </div>
        </ErrorMessageWrapper>
      </div>

      <ModalErrorMessage
        data={modalErrorData as ErrorResponse}
        setData={setModalErrorData as React.Dispatch<React.SetStateAction<boolean>>}
        title="Criação não concluída"
        message="Algo deu errado e a criação não foi concluída."
      />
    </div>
  );
};

export default PosList;
